import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import React, { useRef, useState } from "react"
import { Modal, View } from "react-native"
import ReactCrop from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"

/**
 * Creates a cropped image from the source image and crop area
 * @param {HTMLImageElement} image - The loaded image element
 * @param {Object} crop - The crop area {x, y, width, height, unit}
 * @returns {Promise<{uri: string, blob: Blob}>} - The cropped image as data URL and blob
 */
const createCroppedImage = (image, crop) => {
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    let pixelCrop

    if (crop.unit === "%") {
        // Convert percentage to natural image pixels
        pixelCrop = {
            x: (crop.x / 100) * image.naturalWidth,
            y: (crop.y / 100) * image.naturalHeight,
            width: (crop.width / 100) * image.naturalWidth,
            height: (crop.height / 100) * image.naturalHeight,
        }
    } else {
        // Convert displayed pixels to natural image pixels
        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        pixelCrop = {
            x: crop.x * scaleX,
            y: crop.y * scaleY,
            width: crop.width * scaleX,
            height: crop.height * scaleY,
        }
    }

    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    )

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error("Canvas is empty"))
                return
            }
            const uri = URL.createObjectURL(blob)
            resolve({ uri, blob })
        }, "image/jpeg", 0.95)
    })
}

/**
 * Image crop modal for web - allows user to crop image before analysis
 * Uses react-image-crop for resizable crop area
 */
const ImageCropModal = ({
    visible,
    imageUri,
    onConfirm,
    onCancel,
}) => {
    const [crop, setCrop] = useState({
        unit: "%",
        x: 0,
        y: 0,
        width: 100,
        height: 100,
    })
    const [isProcessing, setIsProcessing] = useState(false)
    const imageRef = useRef(null)

    const handleConfirm = async () => {
        if (!imageRef.current || !crop.width || !crop.height) {
            // No crop selected, use original
            onConfirm(null)
            return
        }

        setIsProcessing(true)
        try {
            const { uri, blob } = await createCroppedImage(imageRef.current, crop)
            onConfirm({
                uri,
                blob,
                name: `cropped_${Date.now()}.jpg`,
                mimeType: "image/jpeg",
            })
        } catch (error) {
            console.error("Error cropping image:", error)
            // Fallback to original image if crop fails
            onConfirm(null)
        } finally {
            setIsProcessing(false)
        }
    }

    const handleSkip = () => {
        // Skip cropping, use original image
        onConfirm(null)
    }

    if (!visible || !imageUri) return null

    return (
        <Modal
            visible={visible}
            transparent
            animationType="fade"
        >
            <View style={{
                flex: 1,
                backgroundColor: "rgba(0, 0, 0, 0.9)",
            }}>
                {/* Header */}
                <View style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: 20,
                    backgroundColor: theme.colors.primary,
                }}>
                    <TextM
                        fontSize={18}
                        fontWeight="Bold"
                        color={theme.colors.white}
                    >
                        Recadrer l'image
                    </TextM>
                    <TextM
                        fontSize={13}
                        color={theme.colors.white}
                        style={{ opacity: 0.8 }}
                    >
                        Ajustez la zone à analyser
                    </TextM>
                </View>

                {/* Crop area */}
                <View style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    padding: 20,
                    overflow: "hidden",
                }}>
                    <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        style={{
                            maxHeight: "100%",
                            maxWidth: "100%",
                        }}
                    >
                        <img
                            ref={imageRef}
                            src={imageUri}
                            alt="Image à recadrer"
                            style={{
                                maxHeight: "calc(100vh - 220px)",
                                maxWidth: "100%",
                                objectFit: "contain",
                            }}
                            crossOrigin="anonymous"
                        />
                    </ReactCrop>
                </View>

                {/* Instructions */}
                <View style={{
                    padding: 10,
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    alignItems: "center",
                }}>
                    <TextM color={theme.colors.white} fontSize={12} style={{ opacity: 0.8 }}>
                        Glissez les bords pour ajuster la zone de recadrage
                    </TextM>
                </View>

                {/* Footer buttons */}
                <View style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    padding: 20,
                    backgroundColor: theme.colors.white,
                    gap: 15,
                }}>
                    <FilledButtonM
                        mode="outlined"
                        onPress={onCancel}
                        label="Annuler"
                        style={{ flex: 1 }}
                        disabled={isProcessing}
                    />
                    <FilledButtonM
                        mode="text"
                        onPress={handleSkip}
                        label="Passer"
                        style={{ flex: 1 }}
                        disabled={isProcessing}
                    />
                    <FilledButtonM
                        mode="contained"
                        onPress={handleConfirm}
                        label="Valider"
                        style={{ flex: 1 }}
                        loading={isProcessing}
                        buttonColor={theme.colors.primary}
                        textColor={theme.colors.white}
                    />
                </View>
            </View>
        </Modal>
    )
}

export default ImageCropModal
